import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { LayoutAuth } from "#/layouts/layout.auth";
import {
  getSignupStep3DataFn,
  getSignupValidStepsFn,
  submitSignupStep3Fn,
} from "#/modules/generic.signup/signup.functions";
import { op, syncOpenPanelUser } from "#/modules/util.instrumentation/op";
import { captureUnexpectedError } from "#/modules/util.errors/capture";

export const Route = createFileRoute("/$locale/{-$schoolyear}/signup/step/3")({
  loader: async ({ params }) => {
    // Guard: steps 1 and 2 must have been completed.
    const validSteps = await getSignupValidStepsFn();
    if (!validSteps.includes(1)) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/signup/step/1",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
    if (!validSteps.includes(2)) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/signup/step/2",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
    return getSignupStep3DataFn();
  },
  component: SignupStep3Page,
});

function SignupStep3Page() {
  const loaderData = Route.useLoaderData();
  const params = Route.useParams();
  const navigate = useNavigate();
  const submitStep3 = useServerFn(submitSignupStep3Fn);

  const register = useMutation({
    mutationFn: () => submitStep3({ data: {} }),
    onError: (error) => captureUnexpectedError(error, "signup-step-3"),
    onSuccess: (res) => {
      if (!res) {
        // Identify before tracking: the account exists now, and identifying first makes
        // registration_completed itself profile-attributed (reports count on the stored
        // profile_id; OpenPanel only stitches anonymous events in the profile timeline).
        if ("data" in loaderData) {
          syncOpenPanelUser(
            {
              email: loaderData.data.email,
              firstName: loaderData.data.name,
              lastName: loaderData.data.surname,
            },
            null,
          );
        }
        void op.track("registration_completed", { source: "self_serve" });
        void navigate({
          to: "/$locale/{-$schoolyear}/signup/step/success",
          params: { locale: params.locale, schoolyear: params.schoolyear },
        });
      }
    },
  });

  const apiError =
    register.data?.formError ?? (register.isError ? m["common.something_went_wrong"]() : undefined);

  if ("formError" in loaderData) {
    return (
      <LayoutAuth showVisuals={false}>
        <Card className="w-full">
          <CardContent className="flex flex-col gap-4 pt-6">
            <Alert variant="destructive">
              <AlertDescription>{loaderData.formError}</AlertDescription>
            </Alert>
            <Button
              variant="secondary"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/signup/step/1"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
            >
              {m["common.actions.try_again"]()}
            </Button>
          </CardContent>
        </Card>
      </LayoutAuth>
    );
  }

  const { data } = loaderData;

  return (
    <LayoutAuth showVisuals={false}>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{m["signup.step3.title"]()}</CardTitle>
          <p className="text-muted-foreground text-sm">{m["signup.step3.subtitle"]()}</p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {apiError && (
            <div className="flex flex-col gap-2">
              <Alert variant="destructive">
                <AlertDescription>{apiError}</AlertDescription>
              </Alert>
              <Button
                variant="destructive"
                render={
                  <Link
                    from={Route.fullPath}
                    to="/$locale/{-$schoolyear}/signup/step/1"
                    params={(prev: Record<string, string>) => prev}
                  />
                }
                nativeButton={false}
              >
                {m["common.actions.try_again"]()}
              </Button>
            </div>
          )}

          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b">
                <td className="py-1.5 pr-4 font-semibold opacity-60">
                  {m["common.fields.first_name"]()}
                </td>
                <td className="py-1.5">{data.name}</td>
              </tr>
              <tr className="border-b">
                <td className="py-1.5 pr-4 font-semibold opacity-60">
                  {m["common.fields.last_name"]()}
                </td>
                <td className="py-1.5">{data.surname}</td>
              </tr>
              <tr className="border-b">
                <td className="py-1.5 pr-4 font-semibold opacity-60">
                  {m["common.fields.email"]()}
                </td>
                <td className="py-1.5">{data.email}</td>
              </tr>
              {data.brin && (
                <tr>
                  <td className="py-1.5 pr-4 font-semibold opacity-60">BRIN</td>
                  <td className="py-1.5">{data.brin}</td>
                </tr>
              )}
            </tbody>
          </table>

          {!apiError && (
            <Button
              onClick={() => register.mutate()}
              isPending={register.isPending}
              className="mt-2 w-full"
            >
              {m["common.actions.registreren"]()}
            </Button>
          )}
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
